import type { PropType } from "vue";
import type { OptionSelect } from "@/components/helpers/types";
import {
  ComboboxCancel,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxRoot,
  ComboboxSeparator,
  ComboboxViewport,
} from "reka-ui";
import { defineComponent } from "vue";

import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import Icon from "../icon/Icon";

export type PointerDownOutsideEvent = CustomEvent<{
  originalEvent: PointerEvent;
}>;

const Root = defineComponent({
  name: "Command",
  emits: {
    "update:modelValue": (_value: string | object | string[] | object[]) => true,
    "update:open": (_value: boolean) => true,
  },
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    modelValue: p.custom<string | object | string[] | object[]>([String, Object, Array]),

    open: p.boolean(true),
    multiple: p.boolean(false),
    defaultOpen: p.boolean(false),
    disabled: p.boolean(),
    /**
     * Disable Reka's built-in text filter. Use when the caller owns filtering
     * (for example a palette that ranks with Fuse) and renders the final list.
     */
    ignoreFilter: p.boolean(false),
    /** Whether Reka clears the search term after a value is selected. */
    resetSearchTermOnSelect: p.boolean(true),
    /** Whether Reka clears the search term when the input blurs. */
    resetSearchTermOnBlur: p.boolean(true),
  },
  setup(props, { slots, attrs, emit }) {
    return () => (
      <ComboboxRoot
        multiple={props.multiple}
        asChild={props.asChild}
        as={props.as}
        modelValue={props.modelValue}
        onUpdate:modelValue={(val: any) => emit("update:modelValue", val)}
        open={props.open}
        onUpdate:open={(val) => emit("update:open", val)}
        defaultOpen={props.defaultOpen}
        disabled={props.disabled}
        ignoreFilter={props.ignoreFilter}
        resetSearchTermOnSelect={props.resetSearchTermOnSelect}
        resetSearchTermOnBlur={props.resetSearchTermOnBlur}
        class={cn("flex h-full w-full flex-col overflow-hidden rounded-md", attrs.class ?? " ")}
      >
        {slots.default?.()}
      </ComboboxRoot>
    );
  },
});

const Input = defineComponent({
  name: "CommandInput",
  emits: {
    "update:modelValue": (_value: string) => true,
  },
  props: {
    asChild: p.boolean(false),
    disabled: p.boolean(false),
    autoFocus: p.boolean(false),
    placeholder: p.string(""),
    modelValue: p.string(),
    displayValue: p.func<(value: unknown) => string>(),
  },
  setup(props, { slots, attrs, emit }) {
    return () => (
      <div class="flex items-center border-b px-3" cmdk-input-wrapper>
        <Icon icon="lucide:search" class="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <ComboboxInput
          {...props}
          {...attrs}
          modelValue={props.modelValue}
          onUpdate:modelValue={(value: string) => emit("update:modelValue", value)}
          auto-focus
          class={cn(
            "flex h-9 w-full rounded-md rounded-t-lg border-none bg-transparent px-0 py-3 text-sm outline-hidden placeholder:text-muted-foreground/50 focus:border-none focus:outline-hidden focus:ring-0 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-muted-foreground/50",
            attrs.class ?? " "
          )}
        />
        {slots.default?.()}
      </div>
    );
  },
});

const Empty = defineComponent({
  name: "CommandEmpty",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <ComboboxEmpty {...props} class={cn("py-6 text-center text-sm", attrs.class ?? " ")}>
        {slots.default?.()}
      </ComboboxEmpty>
    );
  },
});

const Group = defineComponent({
  name: "CommandGroup",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    heading: p.string(""),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <ComboboxGroup
        {...props}
        class={cn("overflow-hidden p-1 text-foreground", attrs.class ?? " ")}
      >
        {props.heading && (
          <ComboboxLabel class="px-2 py-1.5 text-xs font-medium text-muted-foreground dark:text-muted-foreground">
            {props.heading}
          </ComboboxLabel>
        )}
        {slots.default?.()}
      </ComboboxGroup>
    );
  },
});

const Item = defineComponent({
  name: "CommandItem",
  emits: {
    select: (_value: string | OptionSelect) => true,
  },
  props: {
    value: {
      type: [String, Object] as PropType<string | OptionSelect>,
      default: "",
    },
    textValue: p.string(""),
    asChild: p.boolean(false),
    disabled: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
  },
  setup(props, { slots, attrs, emit }) {
    return () => (
      <ComboboxItem
        value={props.value}
        //@ts-ignore
        textValue={props.textValue}
        onSelect={() => {
          emit("select", props.value);
        }}
        disabled={props.disabled}
        class={cn(
          "relative flex cursor-default select-none items-center overflow-hidden truncate rounded-sm px-2 py-1.5 text-sm outline-hidden data-disabled:pointer-events-none data-highlighted:bg-muted/70 data-highlighted:text-foreground data-disabled:opacity-50",
          attrs.class ?? " "
        )}
      >
        {slots.default?.()}
      </ComboboxItem>
    );
  },
});

const Separator = defineComponent({
  name: "CommandSeparator",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
  },
  setup(props, { slots, attrs }) {
    return () => (
      <ComboboxSeparator {...props} class={cn("-mx-1 h-px bg-border", attrs.class ?? " ")}>
        {slots.default?.()}
      </ComboboxSeparator>
    );
  },
});

const List = defineComponent({
  name: "CommandList",
  inheritAttrs: false,
  emits: {
    escapeKeyDown: (_event: KeyboardEvent) => true,
    pointerDownOutside: (_event: PointerDownOutsideEvent) => true,
    closeAutoFocus: (_event: Event) => true,
  },
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    sideOffset: p.number(4),
    side: p.string<"bottom" | "top">("bottom"),
    align: p.string<"end" | "start" | "center">("end"),
    alignOffset: p.number(0),
    avoidCollisions: p.boolean(true),
    collisionBoundary: p.custom<Element | (Element | null)[] | null>([]),
    collisionPadding: p.number(4),
    arrowPadding: p.number(4),
    hideWhenDetached: p.boolean(false),
    updatePositionStrategy: p.string<"always" | "optimized">("optimized"),
    onPlaced: p.func<() => void>(),
    prioritizePosition: p.boolean(false),
    bodyLock: p.boolean(false),
    disableOutsidePointerEvents: p.boolean(false),
    /** Extra classes for the scrollable viewport (defaults keep the 280px cap). */
    viewportClass: p.string(""),
  },
  setup(props, { slots, emit, attrs }) {
    return () => (
      <ComboboxContent
        asChild={props.asChild}
        as={props.as}
        class={attrs.class ?? undefined}
        sideOffset={props.sideOffset}
        side={props.side}
        align={props.align}
        alignOffset={props.alignOffset}
        avoidCollisions={props.avoidCollisions}
        collisionBoundary={props.collisionBoundary}
        collisionPadding={props.collisionPadding}
        arrowPadding={props.arrowPadding}
        hideWhenDetached={props.hideWhenDetached}
        updatePositionStrategy={props.updatePositionStrategy}
        prioritizePosition={props.prioritizePosition}
        bodyLock={props.bodyLock}
        disableOutsidePointerEvents={props.disableOutsidePointerEvents}
        onEscapeKeyDown={(event: KeyboardEvent) => {
          emit("escapeKeyDown", event);
        }}
        onPointerDownOutside={(event: PointerDownOutsideEvent) => emit("pointerDownOutside", event)}
      >
        <ComboboxViewport class={cn("max-h-[280px] min-w-0", props.viewportClass)}>
          <div role="presentation">{slots.default?.()}</div>
        </ComboboxViewport>
        {slots.footer?.()}
      </ComboboxContent>
    );
  },
});

const Footer = defineComponent({
  name: "CommandFooter",
  setup(_, { slots, attrs }) {
    return () => <div class={cn("border-t p-1", attrs.class ?? " ")}>{slots.default?.()}</div>;
  },
});

const Cancel = ComboboxCancel;

export default {
  Root,
  Input,
  Empty,
  Group,
  Separator,
  Item,
  List,
  Footer,
  Cancel,
};
